import * as React from 'react';

import { translate } from '~l10n/client';

import { CreateQuizEventAdapter } from '~analytics/adapters/CreateQuizEventAdapter';
import { UIButton } from '~shared/components/buttons';
import { Container } from '~shared/components/Container/Container';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { Inline } from '~shared/components/Inline';
import { toast } from '~shared/components/Toast';
import {
  CopyToWorkspaceProvider,
  useCopyDialog,
} from '~teacher/components/quizzes/CopyMoveDialog/CopyToWorkspaceDialog/CopyToWorkspaceContext';
import { SelectFolder } from '~teacher/components/quizzes/CopyMoveDialog/CopyToWorkspaceDialog/SelectFolder';
import { SelectWorkspace } from '~teacher/components/quizzes/CopyMoveDialog/CopyToWorkspaceDialog/SelectWorkspace';
import { getCopyToWorkspaceDialogTitle } from '~teacher/components/quizzes/CopyMoveDialog/CopyToWorkspaceDialog/utils';
import { useSelectQuizFolder } from '~teacher/components/quizzes/CopyMoveDialog/useSelectQuizFolder';
import { useQuizzes } from '~teacher/hook';
import { useCopyPersonalToWorkspace } from '~teacher/hook/workspaces/useCopyPersonalToWorkspace';

// logic for copying
function useCopyToWorkspaceMutation({
  setIsCopyToWorkspaceDisabled,
  workspaceId,
  parentId,
  quizIds,
  onClose,
}: {
  setIsCopyToWorkspaceDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  workspaceId: string;
  quizIds: number[];
  parentId: string;
  onClose: (success: boolean, newParentFolder?: number) => void;
}) {
  const copyToWorkspaceMutationResult = useCopyPersonalToWorkspace();

  const onCopyToWorkspace = () => {
    setIsCopyToWorkspaceDisabled(true);
    copyToWorkspaceMutationResult.mutate(
      {
        workspaceId: workspaceId,
        quizIds: quizIds,
        parentId: parentId,
      },
      {
        onSuccess: () => {
          toast(translate('Creating copies'));
          new CreateQuizEventAdapter({
            creation_method: 'to_workspace',
            source_workspace_id: null,
            destination_workspace_id: workspaceId,
          }).sendAnalytics();
          onClose(true);
        },
        onError: () => {
          toast.critical(translate('An error occurred while trying to copy'));
          setIsCopyToWorkspaceDisabled(false);
        },
      }
    );
  };

  return { copyToWorkspaceMutationResult, onCopyToWorkspace };
}

interface Props {
  onClose: (success: boolean, newParentFolder?: number) => void;
  isOpen: boolean;
  itemIdsToMove: Set<string>;
}
export const CopyToWorkspaceDialog = ({
  isOpen,
  onClose,
  itemIdsToMove,
}: Props) => {
  return isOpen ? (
    <CopyToWorkspaceProvider>
      <DialogComponent
        itemIdsToMove={itemIdsToMove}
        isOpen={isOpen}
        onClose={onClose}
      />
    </CopyToWorkspaceProvider>
  ) : null;
};

const DialogComponent = ({ isOpen, onClose, itemIdsToMove }: Props) => {
  const select = useSelectQuizFolder(itemIdsToMove);
  const { workspaceId, parentId, currentStep } = useCopyDialog();
  const enabled = isOpen && itemIdsToMove?.size >= 1;
  const itemsToMove = useQuizzes({
    select: select,
    enabled: enabled,
  });

  const title = getCopyToWorkspaceDialogTitle(itemsToMove?.data);

  const [isCopyToWorkspaceDisabled, setIsCopyToWorkspaceDisabled] =
    React.useState(false);
  const { onCopyToWorkspace } = useCopyToWorkspaceMutation({
    setIsCopyToWorkspaceDisabled: setIsCopyToWorkspaceDisabled,
    workspaceId: workspaceId,
    quizIds: (Array.isArray(itemsToMove?.data) ? itemsToMove.data : []).map(
      (quiz) => quiz.id
    ),
    parentId,
    onClose: onClose,
  });
  return (
    <Dialog
      closeLabel={translate('Close')}
      open={isOpen}
      onClose={onClose}
      id={'launch-quiz-dialog'}
      title={title}
    >
      {/* dialog doesnt unmount, so move to separate component to unmount properly */}
      {isOpen ? (
        <div className={'w-full h-modal-lg flex flex-col'}>
          {/* top divider */}
          <div className={'pt-4'}>
            <Divider />
          </div>
          {/* steps are rendered inside here */}
          <Container className={'overflow-hidden'} width={1}>
            <div className={'flex-grow overflow-y-scroll'}>
              {currentStep === 0 ? <SelectWorkspace /> : null}
              {currentStep === 1 ? <SelectFolder /> : null}
            </div>
          </Container>
          {/* bottom buttons */}
          <Container
            className={'justify-end'}
            row
            margin={'20px 0 0 '}
            verticalCenter
          >
            <Inline align="right">
              <UIButton onClick={onClose}>{translate('Cancel')}</UIButton>
              <UIButton
                onClick={onCopyToWorkspace}
                isDisabled={!workspaceId || isCopyToWorkspaceDisabled}
                appearance="primary"
                a11yLabel="copy-folder-button"
              >
                {translate('Copy')}
              </UIButton>
            </Inline>
          </Container>
        </div>
      ) : null}
    </Dialog>
  );
};
